import { RegistrationFieldProps } from '../../RegistrationField.types';
import React, {
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { getOccupationsSelectableOptions } from '@betty/details-attestation';
import { Box, Input, TextInput, FlatSelect, Text } from '@betty/moonlight';
import { BottomSheet, SelectableOptionItem } from '@betty/navigation';
import { useSelector } from '@betty/toolkit/state';
import { selectRegistrationFormData } from '../../../../state';
import { industriesWithoutOccupation } from '../../../../state/registrationForm/registrationForm.constants';
import { Pressable, Platform, InteractionManager } from 'react-native';

const isWeb = Platform.OS === 'web';

function OccupationField(
  {
    onValue,
    value,
    placeholderNS,
    placeholderKey,
  }: RegistrationFieldProps<'occupation'>,
  _: ForwardedRef<TextInput>,
) {
  const occupationInputRef = useRef<TextInput>(null);
  const data = useSelector(selectRegistrationFormData);
  const occupations = useMemo(getOccupationsSelectableOptions, []);
  const industry =
    data.industry.charAt(0).toUpperCase() +
    data.industry.slice(1).toLowerCase();
  const disableOccupationSelection =
    industriesWithoutOccupation.includes(industry);

  const options = useMemo(
    () => (!disableOccupationSelection ? occupations : []),
    [occupations, disableOccupationSelection],
  );

  const flatSelectOptions = useMemo(() => {
    return occupations.map((occupation, index) => ({
      id: `occupation-${index}`,
      label: occupation.text,
    }));
  }, [occupations]);

  const onOccupationPressIn = useCallback(() => {
    BottomSheet.open({
      namespace: 'common',
      type: 'InputSelect',
      detached: Platform.select({
        ios: false,
        web: true,
        android: false,
      }),
      data: {
        selectableOptions: options,
        headerNS: 'registration',
        headerKey: 'occupation',
        buttonNS: 'registration',
        buttonKey: 'saveButton',
        centerContent: true,
        maxHeight: '25%',
        defaultValue: value,
        value: value,
        onSelect: (selectedValue: SelectableOptionItem) => {
          onValue(selectedValue?.text);
        },
      },
    });
  }, [options, value, onValue]);

  useEffect(() => {
    InteractionManager.runAfterInteractions(() => {
      occupationInputRef.current?.focus();
    });
  }, []);

  return (
    <Box>
      <Box flexDirection="row" gap="space-200" alignItems="center">
        <Box flex={1}>
          {isWeb ? (
            <FlatSelect
              ref={occupationInputRef}
              scrollEnabled={true}
              hasSearch={true}
              minSearchLength={1}
              headerNS={placeholderNS}
              headerKey={placeholderKey}
              onValue={onValue}
              columns={2}
              options={flatSelectOptions}
              value={value}
            />
          ) : (
            <Pressable onPress={onOccupationPressIn}>
              <Input
                ref={occupationInputRef}
                value={value}
                placeholderNS={placeholderNS}
                placeholderKey={placeholderKey}
                keyboardType="number-pad"
                onChangeText={onValue}
                editable={false}
                onPressIn={onOccupationPressIn}
              />
            </Pressable>
          )}
          {!value && (
            <Text
              variant="body-s"
              color="darkSurfaceSecondary"
              locNS="registration"
              locKey="occupationFooter"
              marginTop="space-200"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default memo(forwardRef(OccupationField));
